import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { saveData } from 'services/api/saveData';
import { encryptData } from 'services/authentication/encryptData';
import { ThemeMode } from 'services/theme/themeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleHalfStroke, faSunBright, faMoon, faMoonStars } from '@fortawesome/pro-light-svg-icons';
import '../../style/scss/navigation.scss';

// The selectable theme modes with their icon and label
// The pro light icons are cast, their types come from a newer fontawesome release than the core
const THEME_OPTIONS: { mode: ThemeMode; icon: IconProp; label: string }[] = [
    { mode: 'auto', icon: faCircleHalfStroke as IconProp, label: 'theme.general.auto_label' },
    { mode: 'light', icon: faSunBright as IconProp, label: 'theme.general.light_label' },
    { mode: 'dark', icon: faMoon as IconProp, label: 'theme.general.dark_label' },
    { mode: 'night', icon: faMoonStars as IconProp, label: 'theme.general.night_label' },
];

// Icon in the navbar with a menu to choose the interface theme of the user
const ThemeSwitcher: React.FC = () => {
    const { t } = useTranslation();
    const { userHash } = useAuthContext();
    const { userPreferences, setUserPreferences } = useSettings();
    const [menuVisible, setMenuVisible] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // The mode currently chosen by the user
    const currentMode: ThemeMode = userPreferences.theme || 'auto';

    // Close the menu when clicking outside it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuVisible && menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuVisible]);

    // Save the chosen theme mode in the user preferences
    const handleSelectMode = async (mode: ThemeMode) => {
        setMenuVisible(false);

        // Skip saving when the mode did not change
        if (mode === currentMode) return;

        // Optimistically update the user preferences so the theme switches right away
        setUserPreferences({ ...userPreferences, theme: mode });

        try {
            // Save the theme in the user preference model
            const response: any = await saveData({
                apiUrl: `patch_user_preference/${userHash}`,
                method: 'patch',
                data: { theme: mode },
            });

            // Store the updated preferences encrypted in the local storage
            if (response?.status === 200) {
                localStorage.setItem('user_preferences', encryptData(JSON.stringify(response.data)));
            }
        } catch (error) {
            console.error('Error saving the theme preference', error);
        }
    };

    return (
        <div className='theme-switcher' ref={menuRef}>

            {/* The fixed sun icon which opens the menu, with a tooltip while the menu is closed */}
            <div className='theme-icon' onClick={() => setMenuVisible(!menuVisible)}>
                <FontAwesomeIcon icon={faSunBright as IconProp} />
                {!menuVisible && (
                    <span className='theme-tooltip'>{t('theme.general.tooltip')}</span>
                )}
            </div>

            {/* The menu with the theme modes */}
            {menuVisible && (
                <div className='theme-menu'>
                    {THEME_OPTIONS.map(option => (
                        <div key={option.mode}
                             className={`theme-menu-item ${option.mode === currentMode ? 'active' : ''}`}
                             onClick={() => handleSelectMode(option.mode)}>
                            <span className='theme-menu-icon'>
                                <FontAwesomeIcon icon={option.icon} />
                            </span>
                            {t(option.label)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ThemeSwitcher;
